import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { api } from "@/lib/auth-context";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ loading: true, error: null, already: false });

  useEffect(() => {
    if (!token) {
      setState({ loading: false, error: "Missing verification token in the URL.", already: false });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        if (cancelled) return;
        setState({ loading: false, error: null, already: !!data?.already_verified });
      } catch (err) {
        if (cancelled) return;
        setState({
          loading: false,
          error: err?.response?.data?.detail || "Verification link is invalid or expired.",
          already: false,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div data-testid="verify-email-page" className="min-h-screen bg-[#FBF9F4] flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-2xl p-8 text-center space-y-5">
        {state.loading && (
          <>
            <Loader2 className="w-8 h-8 text-[#C5A059] animate-spin mx-auto" />
            <p className="text-sm text-slate-600">Confirming your email…</p>
          </>
        )}
        {!state.loading && !state.error && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-[#FFF8E7] border-2 border-[#C5A059] flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-[#C5A059]" />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-[#0A192F]">
              {state.already ? "Already verified" : "Email verified"}
            </h1>
            <p className="text-sm text-slate-600">
              {state.already
                ? "Your email was already confirmed — you're good to go."
                : "Thanks — your email is confirmed."}
            </p>
            <Link to="/">
              <Button
                data-testid="verify-back-home"
                className="w-full h-11 rounded-lg bg-[#0A192F] hover:bg-[#112A46] text-white font-semibold"
              >
                Continue to dashboard
              </Button>
            </Link>
          </>
        )}
        {!state.loading && state.error && (
          <>
            <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto" />
            <h1 className="font-heading text-xl font-semibold text-[#0A192F]">
              We couldn't verify that link
            </h1>
            <p data-testid="verify-error" className="text-sm text-slate-600">
              {state.error}
            </p>
            <Link to="/login" className="text-sm text-[#C5A059] underline">
              Back to login
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
